import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import { Spinner } from '../../../shared/components/Spinner';
import {
  buildSetupSteps,
  clampStepIndex,
  emptySetupRecord,
  readSetupRecord,
  SETUP_CHANNELS,
  SetupChannelId,
  SetupRecord,
  writeSetupRecord,
} from '../setupModel';
import { useSetupBilling } from '../useSetupBilling';
import { SetupConnectStep } from './SetupConnectStep';
import { SetupInstructionHandle, SetupInstructionStep } from './SetupInstructionStep';
import styles from './WorkspaceSetupPage.module.css';

export const WorkspaceSetupPage: React.FC = () => {
  const { t } = useTranslation('workspaces');
  const navigate = useNavigate();
  const { auth, hasPermission } = useAuth();
  const tenantId = auth?.tenantId ?? null;
  const canManageConnectors = hasPermission(PermissionScopes.connectorsManage);
  const canManageBilling = hasPermission(PermissionCodes.adminAccess) || hasPermission(PermissionCodes.companiesManage);
  const canManageAi = hasPermission([PermissionCodes.aiConfigManage]);
  const canOpenInbox = hasPermission(PermissionScopes.conversationsRead);
  const instructionRef = React.useRef<SetupInstructionHandle>(null);
  const billing = useSetupBilling();
  const [planError, setPlanError] = React.useState<string | null>(null);

  const [record, setRecord] = React.useState<SetupRecord | null>(null);
  const [draftChannels, setDraftChannels] = React.useState<SetupChannelId[]>([]);
  const [advancing, setAdvancing] = React.useState(false);

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }

    const existing = readSetupRecord(tenantId) ?? emptySetupRecord();
    const next = { ...existing, stepIndex: clampStepIndex(existing) };
    writeSetupRecord(tenantId, next);
    setRecord(next);
    setDraftChannels(next.channels);
  }, [tenantId]);

  const steps = buildSetupSteps(record?.channels ?? []);
  const step = record ? (steps[record.stepIndex] ?? steps[0]) : null;

  const stepIndex = record?.stepIndex ?? -1;
  const stepKind = step?.kind;

  React.useEffect(() => {
    if (stepKind === 'channels' && record) {
      setDraftChannels(record.channels);
    }
  }, [record, stepIndex, stepKind]);

  const persist = React.useCallback((next: SetupRecord) => {
    if (!tenantId) {
      return;
    }

    const clamped = { ...next, stepIndex: clampStepIndex(next) };
    writeSetupRecord(tenantId, clamped);
    setRecord(clamped);
  }, [tenantId]);

  if (!record || !step) {
    return (
      <div className={styles.page}>
        <Spinner text={t('setup.loading')} />
      </div>
    );
  }

  const stepNumber = record.stepIndex + 1;

  const leave = (phase: SetupRecord['phase'], path: string) => {
    persist({ ...record, phase });
    navigate(path, { replace: true });
  };

  const goTo = (stepIndex: number, channels = record.channels) => {
    persist({ ...record, phase: 'active', channels, stepIndex });
  };

  const canStartTrial = canManageBilling
    && !billing.channelsOpen
    && !billing.loading
    && billing.workspace?.trialAvailable !== false;

  const continueStep = async () => {
    if (step.kind === 'plan' && canStartTrial) {
      setAdvancing(true);
      setPlanError(null);
      try {
        await billing.startTrial();
      } catch (startError) {
        setPlanError(startError instanceof Error ? startError.message : t('setup.plan.failed'));
        return;
      } finally {
        setAdvancing(false);
      }
    }

    if (step.kind === 'channels') {
      goTo(record.stepIndex + 1, draftChannels);
      return;
    }

    if (step.kind === 'instruction') {
      setAdvancing(true);
      try {
        const saved = await instructionRef.current?.saveIfNeeded();
        if (!saved) {
          return;
        }
      } finally {
        setAdvancing(false);
      }
    }

    if (step.kind === 'done') {
      leave('done', canOpenInbox ? '/conversations' : '/');
      return;
    }

    goTo(record.stepIndex + 1);
  };

  const skipStep = () => {
    if (step.kind === 'channels') {
      goTo(record.stepIndex + 1, []);
      return;
    }

    if (step.kind === 'done') {
      leave('paused', canOpenInbox ? '/conversations' : '/');
      return;
    }

    goTo(record.stepIndex + 1);
  };

  const toggleChannel = (channel: SetupChannelId) => {
    setDraftChannels(current => (
      current.includes(channel)
        ? current.filter(item => item !== channel)
        : [...current, channel]
    ));
  };

  const continueLabel = step.kind === 'welcome'
    ? t('setup.start')
    : step.kind === 'plan' && canStartTrial
      ? (advancing ? t('setup.plan.starting') : t('setup.plan.start'))
      : step.kind === 'done'
        ? t('setup.done.openInbox')
        : advancing
          ? t('setup.saving')
          : t('setup.continue');

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <p className={styles.progressLabel}>
          {t('setup.progress', { current: stepNumber, total: steps.length })}
        </p>
        <button type="button" className={styles.textButton} onClick={() => leave('paused', '/')}>
          {t('setup.skipSetup')}
        </button>
      </div>
      <div className={styles.progressTrack} aria-hidden>
        <span className={styles.progressFill} style={{ width: `${(stepNumber / steps.length) * 100}%` }} />
      </div>

      <section className={styles.card}>
        <h1 className={styles.title}>
          {step.kind === 'welcome' ? t('setup.welcome.title') : null}
          {step.kind === 'plan' ? t(billing.channelsOpen ? 'setup.plan.alreadyTitle' : billing.workspace?.trialAvailable === false ? 'setup.plan.unavailableTitle' : 'setup.plan.title') : null}
          {step.kind === 'channels' ? t('setup.channels.title') : null}
          {step.kind === 'connect' ? t(`setup.connect.${step.channel}Title`) : null}
          {step.kind === 'instruction' ? t(`setup.instructions.${step.key}.title`) : null}
          {step.kind === 'done' ? t('setup.done.title') : null}
        </h1>

        {step.kind === 'welcome' ? (
          <div>
            <p className={styles.lead}>{t('setup.welcome.lead')}</p>
            <ul className={styles.welcomeList}>
              <li>{t('setup.welcome.pointPlan')}</li>
              <li>{t('setup.welcome.pointChannels')}</li>
              <li>{t('setup.welcome.pointInstructions')}</li>
              <li>{t('setup.welcome.pointSkip')}</li>
            </ul>
          </div>
        ) : null}

        {step.kind === 'plan' ? (
          <div>
            {billing.loading ? <Spinner text={t('setup.plan.loading')} /> : null}
            {billing.channelsOpen ? (
              <p className={styles.lead}>{t('setup.plan.alreadyLead')}</p>
            ) : billing.workspace?.trialAvailable === false ? (
              <>
                <p className={styles.lead}>{t('setup.plan.unavailableLead')}</p>
                <Link to="/plans">{t('setup.plan.seePlans')}</Link>
              </>
            ) : !canManageBilling ? (
              <p className={styles.lead}>{t('setup.plan.noPermission')}</p>
            ) : (
              <>
                <p className={styles.lead}>{t('setup.plan.lead')}</p>
                <ul className={styles.welcomeList}>
                  <li>{t('setup.plan.pointTrial')}</li>
                  <li>{t('setup.plan.pointChannels')}</li>
                  <li>{t('setup.plan.pointNoCard')}</li>
                </ul>
              </>
            )}
            {planError ? <p className={styles.error}>{planError}</p> : null}
            {billing.error ? <p className={styles.error}>{billing.error}</p> : null}
          </div>
        ) : null}

        {step.kind === 'channels' ? (
          <div>
            <p className={styles.lead}>{t('setup.channels.lead')}</p>
            <div className={styles.channelGrid}>
              {SETUP_CHANNELS.map(channel => {
                const selected = draftChannels.includes(channel);
                return (
                  <button
                    key={channel}
                    type="button"
                    className={`${styles.channelCard} ${selected ? styles.channelCardSelected : ''}`}
                    aria-pressed={selected}
                    onClick={() => toggleChannel(channel)}
                  >
                    <span className={`${styles.channelMark} ${styles[`mark_${channel}`]}`} aria-hidden>
                      {t(`setup.channels.${channel}Mark`)}
                    </span>
                    <span>
                      <span className={styles.channelName}>{t(`setup.channels.${channel}`)}</span>
                      <span className={styles.channelDetail}>{t(`setup.channels.${channel}Detail`)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {step.kind === 'connect' ? (
          <SetupConnectStep
            channel={step.channel}
            canManage={canManageConnectors}
            channelsOpen={billing.channelsOpen}
            canStartTrial={canStartTrial}
            onStartTrial={billing.startTrial}
          />
        ) : null}

        {step.kind === 'instruction' ? (
          <SetupInstructionStep
            key={step.useCaseId}
            ref={instructionRef}
            instructionKey={step.key}
            useCaseId={step.useCaseId}
            canManage={canManageAi}
          />
        ) : null}

        {step.kind === 'done' ? (
          <div>
            <p className={styles.lead}>{t('setup.done.lead')}</p>
            {record.channels.length > 0 ? (
              <ul className={styles.welcomeList}>
                {record.channels.map(channel => (
                  <li key={channel}>{t(`setup.channels.${channel}`)}</li>
                ))}
              </ul>
            ) : (
              <p className={styles.hint}>{t('setup.done.noChannels')}</p>
            )}
            <p className={styles.hint}>{t('setup.done.later')}</p>
          </div>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => goTo(record.stepIndex - 1)}
            disabled={record.stepIndex === 0 || advancing}
          >
            {t('setup.back')}
          </button>
          <div className={styles.actionsRight}>
            {step.kind !== 'welcome' ? (
              <button type="button" className={styles.textButton} onClick={skipStep} disabled={advancing}>
                {step.kind === 'done' ? t('setup.done.keepReminder') : t('setup.skip')}
              </button>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={() => { void continueStep(); }} disabled={advancing || (step.kind === 'plan' && billing.loading)}>
              {continueLabel}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkspaceSetupPage;
